import styled from '@emotion/styled'
import { css } from '@emotion/react'

import { SmallPrimaryButton, SmallSecondaryButton } from '../../../components/Button'
import { TABLET } from '../../../guided-toolbox/utils/breakpoints'

// Vertical offset needed to align the side column with the donut arc.
const PLAN_HEADING_MIN_HEIGHT = 48
const PLAN_COLUMN_GAP = 24
const CHART_TOP_OFFSET = `${PLAN_HEADING_MIN_HEIGHT + PLAN_COLUMN_GAP}px`
const CHART_ARC_INSET = 12 // donut's ~12px padding above the arc crest

// Minimum widths determine when the columns wrap.
const MAIN_ROW_GAP = 40
const PLAN_COLUMN_MIN = 420 // includes room for the donut's overhanging callout
const SIDE_COLUMN_MIN = 160
const STACK_WIDTH = PLAN_COLUMN_MIN + SIDE_COLUMN_MIN + MAIN_ROW_GAP

export const PageContainer = styled.div`
  color: ${p => p.theme.darkestGray};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-weight: 400;
  line-height: 22px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 55px 20px 12px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  /* Tablet uses the mobile layout: a centered column capped near mobile width so its alignment rules hold. */
  ${TABLET} {
    max-width: 560px;
    padding-top: 12px;
    gap: 20px;
  }
`

export const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;

  ${TABLET} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
`

export const Greeting = styled.h1`
  margin: 0;
  font-size: 40px;
  font-weight: 300;
  line-height: 58px;
  color: ${p => p.theme.introTextColor || p.theme.primaryColor};

  ${TABLET} {
    font-size: 28px;
    line-height: 1.3;
    text-align: center;
  }
`

export const MainRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${MAIN_ROW_GAP}px;
  /* Define this row as the query container because the collapsible nav makes
     viewport width an unreliable proxy for its width. */
  container: home-main / inline-size;

  ${TABLET} {
    flex-direction: column;
    gap: 35px;
    margin-top: 8px;
  }
`

export const PlanColumn = styled.div`
  flex: 44 1 0;
  min-width: ${PLAN_COLUMN_MIN}px;
  display: flex;
  flex-direction: column;
  gap: ${PLAN_COLUMN_GAP}px;

  ${TABLET} {
    align-items: center;
    min-width: 0;
    width: 100%;
  }
`

export const SideColumn = styled.div`
  flex: 55 1 0;
  min-width: ${SIDE_COLUMN_MIN}px;
  /* Offset the side column to align its top with the donut arc — unless it holds
     the AI chat panel, which is a full-height card meant to pair with the whole
     plan column rather than hang off the arc (see $flush). */
  margin-top: ${p => (p.$flush ? '0' : `calc(${CHART_TOP_OFFSET} + ${CHART_ARC_INSET}px)`)};

  /* Remove the chart-alignment offset when the columns wrap. */
  @container home-main (max-width: ${STACK_WIDTH - 1}px) {
    margin-top: 0;
  }

  ${TABLET} {
    margin-top: 0;
    min-width: 0;
    width: 100%;
  }
`

export const SectionHeading = styled.h2`
  margin: 0;
  color: ${p => p.theme.lightestGray};
  font-family: 'Open Sans';
  font-size: 24px;
  font-weight: 300;
  line-height: normal;

  /* Visually align the heading with the status pill without affecting row height. */
  transform: translateY(-2px);

  ${TABLET} {
    text-align: center;
  }
`

export const PlanHeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  /* Reserve the row height used to align the side column with the chart. */
  min-height: ${PLAN_HEADING_MIN_HEIGHT}px;

  ${TABLET} {
    flex-direction: column;
    justify-content: center;
    min-height: 0;
    gap: 12px;
  }
`

// Center the chart over the CTA buttons without including the overhanging callout
// in the column width.
export const PlanBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: fit-content;
  min-width: ${p => (p.$minWidth ? `${p.$minWidth}px` : '0')};
  max-width: 100%;

  ${TABLET} {
    width: 100%;
    min-width: 0;
  }
`

export const PlanChart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 4px;
`

export const PlanCtaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 26px;
  flex-wrap: wrap;

  ${TABLET} {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }
`

const planCtaButton = css`
  min-width: 185px;
  line-height: 18px;
`

export const GetAdviceButton = styled(SmallPrimaryButton)`
  ${planCtaButton};
`

export const GoalLine = styled.div`
  color: ${p => p.theme.mediumGray};
  font-family: 'Open Sans';
  font-size: 14px;
  text-align: center;
`

// Stacks the AI comment card above the alerts box on narrow viewports, where the
// story keeps both while we decide whether the chat replaces the alerts there.
export const AlertsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

export const ActionItemsBox = styled.div`
  box-sizing: border-box;
  width: 100%;
  border: 2px solid ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  border-radius: 10px;
  background-color: ${p => p.theme.actionItemsBoxColor};
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const ActionItemRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;

  /* Disable interaction and remove the inline baseline gap for the leading icon
     without affecting nested HelpIcons. */
  & > :first-child {
    pointer-events: none;

    .icon {
      display: block;
    }
  }

  color: ${p => p.theme.actionItemsMessageTextColor};
  font-family: 'Open Sans';
  font-size: 16px;
  line-height: 22px;

  ${TABLET} {
    align-items: center;
    text-align: center;
  }
`

export const ActionItemText = styled.span``

export const ToggleAndHelp = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  /* Align the toggle with the greeting text. */
  margin-top: 14px;

  ${TABLET} {
    position: relative;
    margin-top: 0;
  }
`

export const ToggleHelp = styled.span`
  display: inline-flex;
  align-items: center;

  ${TABLET} {
    /* Keep the help icon out of flow so the toggle remains centered. */
    position: absolute;
    left: calc(100% + 10px);
    top: 50%;
    transform: translateY(-50%);
  }
`

export const ToggleTrack = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid ${p => p.theme.lightestGray};
  border-radius: 50px;
  padding: 2px;
  background-color: #fff;
`

export const ToggleSegment = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 15px 0 10px;
  border: 1px solid ${p => (p.active ? p.theme.lightestGray : 'transparent')};
  outline: none;
  border-radius: 50px;
  background-color: ${p => (p.active ? p.theme.piggyToggleActiveColor || '#b8d6a9' : 'transparent')};
  color: ${p => (p.active ? '#02607f' : p.theme.darkestGray)};
  font-family: 'Open Sans';
  font-size: 14px;
  cursor: pointer;
  pointer-events: ${p => (p.active ? 'none' : 'auto')};

  &:hover {
    background-color: ${p => (p.active ? p.theme.piggyToggleActiveColor || '#b8d6a9' : '#f0f2f4')};
  }

  & .icon {
    display: block;
  }
`

export const ModalText = styled.div`
  color: ${p => p.theme.mediumGray};
  font-family: 'Open Sans';
  font-size: 18px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 30px;
`

export const OrangeText = styled.b`
  color: #f48024;
`

export const BoldText = styled.b`
  font-weight: bold;
`

export const RetiredTodayGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;

  ${TABLET} {
    align-items: center;
    width: 100%;
    gap: 16px;
  }
`

export const RetiredTodayRow = styled.div`
  display: flex;
  align-items: center;
  position: relative;

  ${TABLET} {
    width: 100%;
    justify-content: center;
  }
`

export const RetiredTodayCta = styled(SmallSecondaryButton)`
  ${planCtaButton};
`

export const MobileLearnMore = styled.div`
  display: none;

  ${TABLET} {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
  }
`

export const DesktopHelp = styled.span`
  display: inline-flex;
  /* Position the help icon outside the CTA row so it does not affect centering. */
  position: absolute;
  left: calc(100% + 10px);
  top: 0;

  ${TABLET} {
    display: none;
  }
`

export const QuickActionsSection = styled.section`
  margin-top: 48px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${TABLET} {
    margin-top: 15px;
    gap: 12px;
  }
`

export const QuickActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  ${TABLET} {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`

export const QuickActionCard = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
  padding: 20px 24px;
  min-height: 75px;
  border: 2px solid ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  border-radius: 10px;
  background-color: ${p => p.theme.cardHoverBgColor ?? '#f5fafc'};
  color: ${p => p.theme.darkestGray};
  font-family: 'Open Sans';
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  outline: none;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${p => p.theme.ternaryColor ?? '#e7f3f9'};
  }

  ${TABLET} {
    min-height: 0;
    padding: 16px 20px;
    background-color: #ffffff;
  }
`

export const QuickActionIcon = styled.span`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

export const QuickActionLabel = styled.span`
  flex: 1;
`

export const Chevron = styled.span`
  flex: none;
  display: none;
  width: 10px;
  height: 10px;
  border-right: 2px solid ${p => p.theme.primaryColor};
  border-bottom: 2px solid ${p => p.theme.primaryColor};
  transform: rotate(-45deg);

  ${TABLET} {
    display: inline-block;
  }
`
